import { Plural, Trans, t } from "@lingui/macro";
import React, { useRef } from "react";
import { useButton } from "react-aria";
import styled from "styled-components";
import { DestinationMenuEntry } from "~/applications/OfferSearch/Domain/DestinationPage";
import { TravelPurpose } from "~/applications/OfferSearch/Domain/TravelPurpose";
import BookDrawerModal from "~/applications/OfferSearch/Ui/BookDrawer/BookDrawerModal";
import StayRequest from "~/core/StayRequest";
import { useModalController } from "~/core/modal";
import { Route } from "~/core/routes";
import { useLocale } from "~/core/useLocale";
import { Search } from "~/guidelines/Icon";
import { body1, body2, body2Bolder } from "~/guidelines/Typography";

const Pill = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  width: max-content;
  padding: ${({ theme }) => theme.spacing(3, 5)};
  background-color: ${({ theme }) => theme.color.background.lightThird};
  border-radius: 100px;

  ${body1};

  cursor: pointer;
`;

const TextualSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const TextualSummaryRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const TextualLabel = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Separator = styled(TextualLabel)`
  color: ${({ theme }) => theme.color.text.lightSecond};
`;

const DestinationTextualLabel = styled(TextualLabel)`
  ${body2Bolder};
  color: ${({ theme }) => theme.color.text.lightTertiary};
`;

const GuestsTextualLabel = styled(TextualLabel)`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
  text-transform: lowercase;
`;

const DatesTextualLabel = styled(TextualLabel)`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
`;

const NoStayRequest = styled.div`
  ${body1};
  color: ${({ theme }) => theme.color.text.lightThird};
  text-transform: lowercase;
`;

type PillSearchDrawerFormProps = {
  destination: DestinationMenuEntry;
  stayRequest: StayRequest | undefined;
  destinations: Array<DestinationMenuEntry>;
  onSearchRoomOffer: (roomsSearch: {
    destination: DestinationMenuEntry;
    stayRequest: StayRequest;
  }) => void;
  offerMaxCapacity: number | undefined;
  onNewSearch?: (
    newGuests: {
      nbAdults: number;
      nbChildren: number;
      nbBabies: number;
    },
    newTravelPurpose: TravelPurpose,
  ) => Route;
};

const PillSearchDrawerForm = ({
  destination,
  stayRequest,
  destinations,
  onSearchRoomOffer,
  offerMaxCapacity,
  onNewSearch,
}: PillSearchDrawerFormProps) => {
  const locale = useLocale();

  const bookDrawerModalController = useModalController(BookDrawerModal);

  const pillRef = useRef();

  const { buttonProps } = useButton(
    {
      onPress: () => {
        bookDrawerModalController.show({
          initialDestinationSlug: destination.seoSlug,
          initialGuests: stayRequest
            ? {
                nbAdults: stayRequest.guests.nbAdult,
                nbChildren: stayRequest.guests.nbChildren,
                nbBabies: stayRequest.guests.nbBabies,
              }
            : undefined,
          initialStayDates: stayRequest
            ? {
                checkIn: stayRequest.checkIn,
                checkOut: stayRequest.checkOut,
              }
            : undefined,
          initialPromoCode: stayRequest?.promoCode,
          destinations: destinations,
          onSearchRoomOffer: onSearchRoomOffer,
          offerMaxCapacity,
          onNewSearch,
        });
      },
    },
    pillRef as any,
  );

  return (
    <Pill
      {...(buttonProps as any)}
      role="button"
      ref={pillRef as any}
      aria-label={t`Edit your search`}
    >
      <Search />

      <TextualSummary>
        <TextualSummaryRow>
          <DestinationTextualLabel>{destination.name}</DestinationTextualLabel>
        </TextualSummaryRow>

        <TextualSummaryRow>
          {stayRequest ? (
            <>
              <DatesTextualLabel>
                {stayRequest.checkIn.toLocaleDateString(locale, {
                  day: "2-digit",
                  month: "short",
                })}
                &nbsp;–&nbsp;
                {stayRequest.checkOut.toLocaleDateString(locale, {
                  day: "2-digit",
                  month: "short",
                })}
              </DatesTextualLabel>

              <Separator>
                <svg
                  width="5"
                  height="5"
                  viewBox="0 0 5 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="2.5" cy="2.61719" r="2" fill="currentColor" />
                </svg>
              </Separator>

              <GuestsTextualLabel>
                <Plural
                  value={stayRequest.nbGuests}
                  one="# Traveler"
                  other="# Travelers"
                />
              </GuestsTextualLabel>
            </>
          ) : (
            <NoStayRequest>
              <Trans>Check availability</Trans>
            </NoStayRequest>
          )}
        </TextualSummaryRow>
      </TextualSummary>
    </Pill>
  );
};

export default PillSearchDrawerForm;
